import {
	BusyIndicator,
	Portal
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { ensureNotNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { IStoreHistoryState } from "client_legacy/Documents/documentStore";
import {
	DocumentLayoutKind,
	DocumentStoreInitializationState,
	DocumentsCommonSettingsModel,
	IDocumentStoreInitializationModel,
	IDocumentStoreSettingsModel,
	setLayout
} from "client/Api/Documents/documents";
import { DocumentStoreType } from "client/Api/Documents/common";
import { clearDmsCache } from "client/Api/Documents/SharePoint/sharepointDocuments";
import {
	Fragment,
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { AppType } from "client/Api/Apps/common";
import {
	BookmarkHistoryState,
	BookmarksStore,
	createBookmarksUrlFromState
} from "./Bookmarks/BookmarksStore";
import styles from "./DocumentPage.module.less";
import {
	createSharePointUrlFromState,
	SharePointActivationContext,
	SharePointHistoryState,
	SharePointStore
} from "./SharePoint/SharePointStore";
import { SharePointDocumentsSettingsModel } from "client/Api/Documents/SharePoint/common";
import { useDocumentsHistoryState } from "./useDocumentsHistoryState";
import {
	areAllStoresNotConfigured,
	didAllConfiguredStoresFailed,
	getConfiguredStoreTypes,
	useInitializer
} from "./useInitializer";
import {
	WebDavActivationContext,
	WebDavHistoryState,
	createWebDavUrlFromState,
	WebDavStore
} from "./WebDav/WebDavStore";
import {
	useGlobalNavigationStore,
	GlobalNavigationCriteria
} from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { GlobalNavigationSidebar } from "../Layout/GlobalNavigationSidebar/GlobalNavigationSidebar";
import {
	Action,
	ActionKind
} from "client_legacy/ndaw/actions";
import { VisibilityConfiguration } from "./SharePoint/DocumentViewStructureCustomization/VisibilityConfiguration";
import { useHandleNavigationInGlobalSubNavigation } from "../Layout/GlobalNavigationSidebar/useGlobalNavigationVisibilityState";
import { ContainsDropOversContext } from "client/Contexts/ContainsDropOversContext";
import { useNoDocumentStoresConfiguredDialog } from "./NoDocumentStoresConfiguredDialog";
import { useDocumentsInitializationFailedDialog } from "./DocumentsInitializationFailedDialog";
import { useUnableToOpenDocumentLocationDialog } from "./UnableToOpenDocumentLocationDialog";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { UploadBatchHandlers } from "./DocumentUploader/useDocumentUploader";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";
import { DocumentUploader, DocumentUploaderHandle } from "./DocumentUploader/DocumentUploader";

export type DocumentPageProps = {
	initialModel: DocumentsViewModel;
};

export const DocumentPage = (props: DocumentPageProps): ReactElement => {
	const {
		initialModel
	} = props;

	const {
		commonSettings,
		storeSpecificSettings
	} = initialModel;

	const handleNavigationInGlobalSubNavigation: () => void = useHandleNavigationInGlobalSubNavigation();
	const [sideBarContainer, setSideBarContainer] = useState<HTMLDivElement | null>(null);
	const [isSharePointSideBarContentLoaded, setIsSharePointSideBarContentLoaded] = useState<boolean>(false);
	const [isRefreshingCache, setIsRefreshingCache] = useState<boolean>(false);
	const [doesSharePointSidebarContainDropOvers, setDoesSharePointSidebarContainDropOvers] = useState<boolean>(false);
	const [doesWebDavSidebarContainDropOvers, setDoesWebDavSidebarContainDropOVers] = useState<boolean>(false);
	const [doesBookmarkSidebarContainDropOvers, setDoesBookmarkSidebarContainDropOVers] = useState<boolean>(false);
	const [isSharePointVisibilityConfigurationVisible, setIsSharePointVisibilityConfigurationVisible] = useState<boolean>(false);
	const [layoutKind, setLayoutKind] = useState<DocumentLayoutKind>(commonSettings.defaultLayout);

	const storeInitializationModels: readonly IDocumentStoreInitializationModel[] | undefined = useInitializer(
		initialModel.storeInitializationModels,
		false
	);

	const [historyState, setHistoryState] = useDocumentsHistoryState();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [noDocumentStoresConfiguredDialog, openNoDocumentStoresConfiguredDialog] = useNoDocumentStoresConfiguredDialog();
	const [documentsInitializationFailedDialog, openDocumentsInitializationFailedDialog] = useDocumentsInitializationFailedDialog();
	const [unableToOpenDocumentLocationDialog, openUnableToOpenDocumentLocationDialog] = useUnableToOpenDocumentLocationDialog();
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);
	const documentUploaderRef: RefObject<DocumentUploaderHandle | null> = useRef<DocumentUploaderHandle>(null);

	const setActiveGlobalNavigationCriteria: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);

	const layoutKindChangeHandler = useCallback(
		async (kind: DocumentLayoutKind): Promise<void> => {
			setLayoutKind(kind);

			try {
				await setLayout(
					kind,
					globalCancellation
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			globalCancellation
		]
	);

	useEffect(
		(): (() => void) => {
			addAnalyticsAppEvent({
				appType: AppType.Documents
			});

			return setActiveGlobalNavigationCriteria({
				type: "app",
				appType: AppType.Documents
			});
		},
		[setActiveGlobalNavigationCriteria, addAnalyticsAppEvent]
	);

	useEffect(
		(): void => {
			if (!storeInitializationModels) {
				return;
			}

			const areAllNotConfigured: boolean = areAllStoresNotConfigured(storeInitializationModels);
			const didAllFailed: boolean = didAllConfiguredStoresFailed(storeInitializationModels);

			if (areAllNotConfigured) {
				openNoDocumentStoresConfiguredDialog({
					isForAdmin: commonSettings.isAdmin,
					onClose: "navigate-to-workspace"
				});
			} else if (didAllFailed) {
				openDocumentsInitializationFailedDialog();
			}

			if (areAllNotConfigured || didAllFailed) {
				return;
			}

			let storeToInitialize: IDocumentStoreInitializationModel | undefined = storeInitializationModels
				?.find(
					x => x.state === DocumentStoreInitializationState.Success
						&& x.storeType === initialModel.activeStoreType
			);

			// The backend does not provide an initialization model for bookmarks, so we produce it client side
			if (!storeToInitialize
				&& initialModel.activeStoreType === DocumentStoreType.Bookmarks
			) {
				storeToInitialize = {
					storeType: DocumentStoreType.Bookmarks,
					state: DocumentStoreInitializationState.Success
				};
			}

			if (!storeToInitialize) {
				if (initialModel.requestedSpecificLocation) {
					// When the user requests a specific location but the store fails to initialize
					// we need to make him aware of it
					openUnableToOpenDocumentLocationDialog();
				}
				return;
			}

			const state: IStoreHistoryState = {
				...storeToInitialize,
				store: storeToInitialize.storeType
			};

			setHistoryState(
				state,
				getUrlForHistoryState(state)
			);
		},
		[
			storeInitializationModels,
			commonSettings.isAdmin,
			setHistoryState,
			initialModel.activeStoreType,
			initialModel.requestedSpecificLocation,
			openDocumentsInitializationFailedDialog,
			openNoDocumentStoresConfiguredDialog,
			openUnableToOpenDocumentLocationDialog
		]
	);

	const storeTypesToDisplay = useMemo(
		(): readonly DocumentStoreType[] => {
			if (!storeInitializationModels) {
				return [];
			}

			const availableStores: readonly IDocumentStoreInitializationModel[] = storeInitializationModels.filter(
				x => x.state !== DocumentStoreInitializationState.NoLicense
			);

			let typesToDisplay: readonly DocumentStoreType[] = getConfiguredStoreTypes(availableStores);

			if (storeInitializationModels.some(x => x.state === DocumentStoreInitializationState.Success)) {
				typesToDisplay = [DocumentStoreType.Bookmarks, ...typesToDisplay];
			}

			return typesToDisplay;
		},
		[storeInitializationModels]
	);

	const clearCacheHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsRefreshingCache(true);
				await clearDmsCache(globalCancellation);
				reloadCurrentPage();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsRefreshingCache(false);
			}
		},
		[alertManager, globalCancellation]
	);

	const sidebarActions = useMemo(
		(): readonly Action<SidebarActionContext>[] => {
			return getSidebarActions(
				{
					customizeSharePointView(): void {
						setIsSharePointVisibilityConfigurationVisible(true);
						handleNavigationInGlobalSubNavigation();
					},
					refreshSharePointCache(): void {
						clearCacheHandler();
						handleNavigationInGlobalSubNavigation();
					},
				}
			);
		},
		[
			handleNavigationInGlobalSubNavigation,
			clearCacheHandler
		]
	);

	const sidebarActionContext = useMemo(
		(): SidebarActionContext => {
			const getIsDocumentViewStructureCustomizationEnabled = (): boolean => {
				if (!storeTypesToDisplay?.some(x => x === DocumentStoreType.SharePoint)) {
					return false;
				}

				const sharePointSettingsModel = ensureNotNil(
					storeSpecificSettings?.find(x => x.storeType === DocumentStoreType.SharePoint)
				) as SharePointDocumentsSettingsModel;

				return sharePointSettingsModel.isDocumentViewStructureCustomizationEnabled;
			};

			return {
				isSharePointSideBarContentLoaded,
				isDocumentViewStructureCustomizationEnabled: getIsDocumentViewStructureCustomizationEnabled()
			};
		},
		[
			isSharePointSideBarContentLoaded,
			storeSpecificSettings,
			storeTypesToDisplay
		]
	);

	const changeNavigationStateHandler = useCallback(
		(state: IStoreHistoryState): void => {
			setHistoryState(
				state,
				getUrlForHistoryState(state)
			);
		},
		[setHistoryState]
	);

	const sharePointSideBarContentLoadedHandler = useCallback(
		(): void => {
			setIsSharePointSideBarContentLoaded(true);
		},
		[]
	);

	const documentUploadHandler = useCallback(
		(
			files: readonly FileSelectionEntry[],
			handlers: UploadBatchHandlers
		): void => {
			documentUploaderRef.current
				?.uploadDocument(
					files,
					handlers
				);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(historyState);
			documentUploaderRef.current
				?.collapse();
		},
		[historyState]
	);

	const renderStore = useCallback(
		(kind: DocumentStoreType): ReactElement | null => {
			if (!sideBarContainer) {
				return null;
			}

			switch (kind)
			{
				case DocumentStoreType.Bookmarks:
					return (
						<BookmarksStore
							navigationState={
								historyState?.store === DocumentStoreType.Bookmarks
									? historyState as BookmarkHistoryState
									: undefined
							}
							onChangeNavigationState={changeNavigationStateHandler}
							sideBarContainer={sideBarContainer}
							onSideBarNavigation={handleNavigationInGlobalSubNavigation}
							onDoesSidebarContainDropOversChange={setDoesBookmarkSidebarContainDropOVers}
							layoutKind={layoutKind}
							onLayoutKindChange={layoutKindChangeHandler}
						/>
					);
				case DocumentStoreType.SharePoint:
					const sharePointInitializationModel: IDocumentStoreInitializationModel = ensureNotNil(
						storeInitializationModels?.find(x => x.storeType === DocumentStoreType.SharePoint)
					);

					const sharePointSettingsModel = ensureNotNil(
						storeSpecificSettings?.find(x => x.storeType === DocumentStoreType.SharePoint)
					) as SharePointDocumentsSettingsModel;

					return (
						<SharePointStore
							hasInitializationError={sharePointInitializationModel.state !== DocumentStoreInitializationState.Success}
							navigationState={
								historyState?.store === DocumentStoreType.SharePoint
									? historyState as SharePointHistoryState | SharePointActivationContext
									: undefined
							}
							onChangeNavigationState={changeNavigationStateHandler}
							onSideBarContentIsLoaded={sharePointSideBarContentLoadedHandler}
							settings={sharePointSettingsModel}
							maxNumberOfFilesToDownloadAsZip={commonSettings.maxNumberOfFilesToDownloadAsZip}
							sideBarContainer={sideBarContainer}
							onSideBarNavigation={handleNavigationInGlobalSubNavigation}
							onDoesSidebarContainDropOversChange={setDoesSharePointSidebarContainDropOvers}
							layoutKind={layoutKind}
							onLayoutKindChange={layoutKindChangeHandler}
							onDocumentUpload={documentUploadHandler}
						/>
					);
				case DocumentStoreType.WebDav:
					const { webDav: webDavConfiguration } = documentsConfiguration();
					const webDavInitializationModel: IDocumentStoreInitializationModel = ensureNotNil(
						storeInitializationModels?.find(x => x.storeType === DocumentStoreType.WebDav)
					);

					if (!webDavConfiguration) {
						throw new Error("WebDav is not available");
					}

					return (
						<WebDavStore
							hasInitializationError={webDavInitializationModel.state !== DocumentStoreInitializationState.Success}
							navigationState={
								historyState?.store === DocumentStoreType.WebDav
									? historyState as WebDavHistoryState | WebDavActivationContext
									: undefined
							}
							onChangeNavigationState={changeNavigationStateHandler}
							sideBarContainer={sideBarContainer}
							onSideBarNavigation={handleNavigationInGlobalSubNavigation}
							onDoesSidebarContainDropOversChange={setDoesWebDavSidebarContainDropOVers}
							webDavConfiguration={webDavConfiguration}
							layoutKind={layoutKind}
							onLayoutKindChange={layoutKindChangeHandler}
							onDocumentUpload={documentUploadHandler}
						/>
					);
				default:
					never(kind);
			}
		},
		[
			sideBarContainer,
			historyState,
			changeNavigationStateHandler,
			storeInitializationModels,
			storeSpecificSettings,
			commonSettings.maxNumberOfFilesToDownloadAsZip,
			handleNavigationInGlobalSubNavigation,
			sharePointSideBarContentLoadedHandler,
			layoutKind,
			layoutKindChangeHandler,
			documentUploadHandler
		]
	);

	return (
		<>
			<div className={styles.root}>
				{isRefreshingCache && (
					<Portal container={document.body}>
						<BusyIndicator />
					</Portal>
				)}

				{storeInitializationModels
					? (
						<>
							<GlobalNavigationSidebar.ActivePanelOverride
								title={localize("_Documents")}
								kind={AppType.Documents}
								actions={sidebarActions}
								actionContext={sidebarActionContext}
							>
								<SidebarContainer
									ref={setSideBarContainer}
									doesSharePointSidebarContainDropOvers={doesSharePointSidebarContainDropOvers}
									doesWebDavSidebarContainDropOvers={doesWebDavSidebarContainDropOvers}
									doesBookmarkSidebarContainDropOvers={doesBookmarkSidebarContainDropOvers}
								/>
							</GlobalNavigationSidebar.ActivePanelOverride>
							{storeTypesToDisplay.map((x) => (
								<Fragment key={x}>
									{renderStore(x)}
								</Fragment>
							))}
							<DocumentUploader
								ref={documentUploaderRef}
							/>
						</>
					)
					: <BusyIndicator />
				}
				{isSharePointVisibilityConfigurationVisible && (
					<VisibilityConfiguration
						onClose={() => setIsSharePointVisibilityConfigurationVisible(false)}
					/>
				)}
			</div>

			{noDocumentStoresConfiguredDialog}
			{documentsInitializationFailedDialog}
			{unableToOpenDocumentLocationDialog}
		</>
	);
};
DocumentPage.displayName = "DocumentPage";

type SidebarContainerProps = {
	doesSharePointSidebarContainDropOvers: boolean;
	doesWebDavSidebarContainDropOvers: boolean;
	doesBookmarkSidebarContainDropOvers: boolean;
};

const SidebarContainer = forwardRef(
	(props: SidebarContainerProps, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			doesSharePointSidebarContainDropOvers,
			doesWebDavSidebarContainDropOvers,
			doesBookmarkSidebarContainDropOvers
		} = props;

		const {
			registerDropOver
		} = useContext(ContainsDropOversContext);

		useEffect(
			(): EffectWithDestructorResult => {
				if (!doesSharePointSidebarContainDropOvers) {
					return undefined;
				}

				return registerDropOver?.();
			},
			[doesSharePointSidebarContainDropOvers, registerDropOver]
		);

		useEffect(
			(): EffectWithDestructorResult => {
				if (!doesWebDavSidebarContainDropOvers) {
					return undefined;
				}

				return registerDropOver?.();
			},
			[doesWebDavSidebarContainDropOvers, registerDropOver]
		);

		useEffect(
			(): EffectWithDestructorResult => {
				if (!doesBookmarkSidebarContainDropOvers) {
					return undefined;
				}

				return registerDropOver?.();
			},
			[doesBookmarkSidebarContainDropOvers, registerDropOver]
		);

		return (
			<div ref={ref} />
		);
	}
);
SidebarContainer.displayName = "SidebarContainer";

export function getUrlForHistoryState(state: IStoreHistoryState): string {
	switch (state.store) {
		case DocumentStoreType.Bookmarks:
			return createBookmarksUrlFromState(state);
		case DocumentStoreType.SharePoint:
			return createSharePointUrlFromState(state);
		case DocumentStoreType.WebDav:
			return createWebDavUrlFromState(state);
		default:
			throw new Error(`Unknown state '${JSON.stringify(state)}'`);
	}
}

export function init(model: DocumentsViewModel): void {
	const host: HTMLElement = ensureNotNil(
		document.getElementById("main-content")
	);
	createRoot(host)
		.render(
			<DocumentPage initialModel={model} />
		);
};

interface DocumentsViewModel {
	commonSettings: DocumentsCommonSettingsModel;
	storeSpecificSettings: IDocumentStoreSettingsModel[];
	storeInitializationModels: IDocumentStoreInitializationModel[];
	activeStoreType: DocumentStoreType;
	requestedSpecificLocation: boolean;
}

type SidebarActionContext = {
	isSharePointSideBarContentLoaded: boolean;
	isDocumentViewStructureCustomizationEnabled: boolean;
};

type SidebarActionHandlers = {
	customizeSharePointView(): void;
	refreshSharePointCache(): void;
};

function getSidebarActions(handlers: SidebarActionHandlers): readonly Action<SidebarActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "sharepoint-customize-view",
			title: localize("_Manage"),
			icon: "gear",
			canExecute(context: SidebarActionContext): boolean {
				return context.isDocumentViewStructureCustomizationEnabled;
			},
			execute(): void {
				handlers.customizeSharePointView();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "sharepoint-refresh-cache",
			icon: "arrows-rotate",
			title: localize("_Refresh"),
			canExecute(context: SidebarActionContext): boolean {
				return context.isSharePointSideBarContentLoaded;
			},
			execute(): void {
				handlers.refreshSharePointCache();
			},
		}
	];
}
